import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ChevronDown, ChevronRight, MoreVertical, Truck, Wrench, FolderOpen, User, Phone, Building2, Calendar, Paperclip, Download } from 'lucide-react';
import CategoryBadge from './CategoryBadge';
import StatusBadge from './StatusBadge';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskTableRow = ({ task, onEdit, onDelete, onStatusChange, onTaskUpdate, isSelected, onToggleSelect, canEdit }) => {
  const [expanded, setExpanded] = useState(false);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Truck Section':
        return <Truck className="h-4 w-4" />;
      case 'Machinery Section':
        return <Wrench className="h-4 w-4" />;
      default:
        return <FolderOpen className="h-4 w-4" />;
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  return (
    <>
      <tr className="hover:bg-slate-50 transition-colors" data-testid={`task-row-${task.id}`}>
        <td className="px-6 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(task.id)}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
            data-testid={`checkbox-${task.id}`}
          />
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 p-1 rounded transition-colors cursor-pointer"
              data-testid={`expand-task-${task.id}`}
              aria-label={expanded ? "Collapse task details" : "Expand task details"}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <div>
              <p className="font-medium text-slate-900" data-testid={`task-title-${task.id}`}>{task.title}</p>
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <CategoryBadge category={task.category} icon={getCategoryIcon(task.category)} />
        </td>
        <td className="px-6 py-4">
          <StatusBadge
            status={task.status}
            taskId={task.id}
            onStatusChange={onStatusChange}
          />
        </td>
        <td className="px-6 py-4 text-sm text-slate-700">{task.assigned_to}</td>
        <td className="px-6 py-4 text-sm text-slate-700">{task.vendor_name}</td>
        <td className="px-6 py-4 text-sm text-slate-700">
          {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : '-'}
        </td>
        <td className="px-6 py-4 text-right">
          {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-slate-100"
                data-testid={`task-menu-${task.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }} 
                data-testid={`edit-task-${task.id}`}
                className="cursor-pointer"
              >
                Edit Task
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                data-testid={`delete-task-${task.id}`}
              >
                Delete Task
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          ) : null}
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`task-details-${task.id}`}>
          <td colSpan={8} className="px-6 py-4 bg-slate-50">
            <div className="space-y-4">
              {task.description && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Description</h4>
                  <p className="text-sm text-slate-700">{task.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Vendor Information</h4>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <User className="h-4 w-4 text-slate-500" />
                      <span>{task.vendor_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Phone className="h-4 w-4 text-slate-500" />
                      <span>{task.vendor_contact}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Work Assignment</h4>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <span>{task.work_assignment}</span>
                  </div>
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">Created By</h4>
                    <p className="text-sm text-slate-700">{task.created_by}</p>
                  </div>
                </div>
              </div>

              {task.attachments && task.attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({task.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {task.attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white px-4 py-2 rounded border border-slate-200"
                        data-testid={`attachment-${index}`}
                      >
                        <div className="flex items-center gap-2">
                          <Paperclip className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-700">{file.filename}</span>
                          <span className="text-xs text-slate-500">
                            ({new Date(file.uploaded_at).toLocaleDateString()})
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadFile(file.file_id, file.filename)}
                          data-testid={`download-attachment-${index}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  Created: {format(new Date(task.created_at), 'MMM dd, yyyy h:mm a')} • 
                  Updated: {format(new Date(task.updated_at), 'MMM dd, yyyy h:mm a')}
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default TaskTableRow;
